import { defineStore } from 'pinia'
import * as Sentry from '@sentry/vue'
import type { AxiosResponse } from 'axios'
import { client } from '@/services/client'
import { handleRequest } from '@/services/handleRequest'
import { useSpace } from '@/store/modules/App/space'
import { useStories } from '@/store/modules/App/stories'
import type { Asset, AssetFolder, Space, Story } from '@/types'
import Throttle from '@utils/promiseThrottle'
import { getAssetBasePath } from '@utils/specifics'
import { t } from '@/utils/helpers/translate'

export type State = {
  assets: Asset[] | undefined
  folders: AssetFolder[]
  total: number
  selectedAssets: Asset[]
  selectedFolder: number | null
  isAssetModalOpen: boolean
  breadcrumbs: AssetFolder[]
  sidebarWidth: number
}

export type AssetParams = {
  page: number
  per_page: number
  in_folder?: number
  sort_by?:
    | 'created_at:asc'
    | 'created_at:desc'
    | 'updated_at:asc'
    | 'updated_at:desc'
    | 'short_filename:asc'
    | 'short_filename:desc'
  search?: string
}

export const useAssets = defineStore('asset', {
  state: (): State => ({
    assets: undefined,
    folders: [],
    total: 0,
    selectedAssets: [],
    selectedFolder: null,
    isAssetModalOpen: false,
    breadcrumbs: [],
    sidebarWidth: 230,
  }),
  getters: {
    getSelectedAssetsIds: (state: State): number[] =>
      state.selectedAssets.map((element) => element.id),
    getSelectedAssets: (state: State): Asset[] => state.selectedAssets,
    getSelectedFolder: (state: State): number | null => state.selectedFolder,
    getAssetModalOpen: (state: State): boolean => state.isAssetModalOpen,
    getAssets: (state: State): Asset[] | undefined => state.assets,
    realSidebarWidth: (state: State): number => state.sidebarWidth,
  },
  actions: {
    setAssetFolders(folders: AssetFolder[]): AssetFolder[] {
      return (this.folders = folders)
    },

    updateAssetInList(newAssetData: Asset): void {
      this.assets?.forEach((a) => {
        if (a.id === newAssetData.id) {
          for (const k in newAssetData) {
            a[k] = newAssetData[k]
          }
        }
      })
    },

    setAssets(
      data: { assets: Asset[] },
      headers?: AxiosResponse['headers'] & { total?: string },
    ): void {
      this.assets = data.assets
      if (headers?.total) {
        this.total = parseInt(headers.total)
      }
    },

    clearAssets(): void {
      this.assets = []
      this.total = 0
      this.folders = []
    },

    selectAsset(element: Asset): void {
      const hasAsset = this.selectedAssets.find((e) => e.id === element.id)
      if (!hasAsset) {
        this.selectedAssets.push(element)
      }
    },

    selectAllAssets(assets: Asset[]): void {
      this.selectedAssets = assets
    },

    deselectAsset(id: number): void {
      this.selectedAssets = this.selectedAssets.filter((element) => {
        return element.id !== id
      })
    },

    clearSelectedAssets(): void {
      this.selectedAssets = []
    },

    clearSelectedFolderLocal(): null {
      return (this.selectedFolder = null)
    },

    setBreadcrumbs(list: AssetFolder[]): AssetFolder[] {
      return (this.breadcrumbs = list)
    },

    clearBreadcrumbs(): AssetFolder[] {
      return (this.breadcrumbs = [])
    },

    setSelectedFolderId(id: number): number {
      return (this.selectedFolder = id)
    },

    openAssetModal(): void {
      this.isAssetModalOpen = true
    },

    closeAssetModal(): void {
      this.isAssetModalOpen = false
    },

    updateFolderNameInList({
      newName,
      folderId,
    }: {
      newName: string
      folderId: number
    }): void {
      const updatedFolder = this.folders.find(
        (folder) => folder.id === folderId,
      )
      if (updatedFolder) {
        updatedFolder.name = newName
      }
    },

    setSelectedFolder(folderId: number): void {
      const folderErrorMessage = 'The folder does not exists!'

      const { folders } = this

      let selectedFolder = folders.find(
        (item: AssetFolder) => item.id === folderId,
      )
      if (!selectedFolder) {
        throw new Error(folderErrorMessage)
      }

      selectedFolder = { ...selectedFolder, isActive: true }

      let breadcrumbs: AssetFolder[] = []
      const currentFolderIndex =
        this.breadcrumbs.findIndex(
          (item: AssetFolder) => item.id === selectedFolder.id,
        ) + 1

      function addParent(parentId: number): void {
        if (parentId) {
          const folder = folders.find(
            (item: AssetFolder) => item.id === parentId,
          )
          if (folder) {
            breadcrumbs.unshift(folder)
            addParent(folder.parent_id)
          }
        }
      }

      if (
        this.breadcrumbs.find(
          (item: AssetFolder) =>
            item.id === selectedFolder.id &&
            item.parent_id === selectedFolder.parent_id,
        )
      ) {
        breadcrumbs = this.breadcrumbs.slice(0, currentFolderIndex)
      } else {
        if (selectedFolder.parent_id) {
          breadcrumbs.push(selectedFolder)
          addParent(selectedFolder.parent_id)
        } else {
          breadcrumbs.push(selectedFolder)
        }
      }

      if (breadcrumbs) {
        this.setSelectedFolderId(folderId)
        this.setBreadcrumbs(breadcrumbs)
      } else {
        throw new Error(folderErrorMessage)
      }
    },

    clearSelectedFolder(): void {
      this.clearSelectedFolderLocal()
      this.clearBreadcrumbs()
    },

    async loadAssetFolders(spaceId: number): Promise<void> {
      const response = await client.get(`spaces/${spaceId}/asset_folders`, {
        region: useSpace().region,
      })
      this.setAssetFolders(response.data.asset_folders)
      delete response.data.asset_folders
    },

    async loadAssets({
      params,
      spaceId,
    }: {
      params: AssetParams
      spaceId: number
    }) {
      if (!spaceId) {
        Sentry.captureMessage(
          'Missing required params: no space ID provided to loadAssets()',
          'error',
        )
        return {
          status: 'unexpectedError',
          error: t('Space ID is required to load the assets.'),
          statusCode: 400,
        }
      }

      const response = await handleRequest({
        apiCall: (): Promise<AxiosResponse<{ assets: Asset[] }>> =>
          client.get(`spaces/${spaceId}/assets`, {
            params,
            region: useSpace().region,
          }),
        expectedErrors: new Map([
          [404, t('No assets found for the selected query.')],
        ]),
      })

      if (response.status === 'success') {
        this.setAssets(response.data, response.headers)
      }

      return response
    },

    async loadSingleAsset({
      assetId,
      spaceId,
    }: {
      assetId: number
      spaceId: number
    }): Promise<Asset> {
      const response = await client.get(`spaces/${spaceId}/assets/${assetId}`, {
        region: useSpace().region,
      })

      return Promise.resolve(response.data)
    },

    async loadSingleAssetByFileName({
      assetFileName,
      spaceId,
    }: {
      assetFileName: string
      spaceId: string
    }): Promise<Asset> {
      const fileName = assetFileName.replace(/^https?:\/\//, '')
      const response = await client.get(
        `spaces/${spaceId}/assets/?search=${fileName}`,
        {
          region: useSpace().region,
        },
      )

      return Promise.resolve(response.data.assets[0])
    },

    async countAssetsReferences(
      assets: Asset[],
    ): Promise<Array<{ id: Asset['id']; total: number }>> {
      const { spaceId } = useSpace()
      const { countReferences } = useStories()
      const references = await countReferences({
        terms: assets.map((asset) => getAssetBasePath(asset.filename)),
        spaceId,
      })
      return assets.map((asset) => ({
        id: asset.id,
        total: references[getAssetBasePath(asset.filename)] ?? 0,
      }))
    },

    async loadAssetReferences(asset: Asset): Promise<Story[]> {
      const { spaceId } = useSpace()
      const { loadStories } = useStories()
      const {
        data: { stories },
      } = await loadStories({
        spaceId,
        apiParameters: {
          page: 1,
          per_page: 25,
          reference_search: [getAssetBasePath(asset.filename)],
        },
        noCommit: true,
      })
      return stories
    },

    async updateAsset({
      newAsset,
      spaceId,
      assetId,
    }: {
      newAsset: Asset
      spaceId: string
      assetId: number
    }): Promise<void> {
      await client.put(
        `spaces/${spaceId}/assets/${assetId}`,
        {
          asset: newAsset,
        },
        {
          region: useSpace().region,
        },
      )

      return this.updateAssetInList(newAsset)
    },

    async bulkUpdateAssetsTags({
      assetsTags,
      spaceId,
    }: {
      assetsTags: { asset_id: number; internal_tag_list_ids: number[] }[]
      spaceId: string | string[]
    }): Promise<void> {
      await client.post(
        `spaces/${spaceId}/assets/bulk_tags_association`,
        {
          assets_tags: assetsTags,
        },
        {
          region: useSpace().region,
        },
      )
    },

    async createAssetFolder(payload: {
      folder: Pick<AssetFolder, 'name' | 'parent_id'>
      spaceId: number
    }): Promise<void> {
      const { folder, spaceId } = payload
      await client.post(`spaces/${spaceId}/asset_folders`, folder, {
        region: useSpace().region,
      })
    },

    async renameAssetFolder({
      newName,
      folderId,
      spaceId,
    }: {
      newName: string
      folderId: number
      spaceId: string
    }): Promise<void> {
      await client.put(
        `spaces/${spaceId}/asset_folders/${folderId}`,
        {
          asset_folder: { name: newName },
        },
        {
          region: useSpace().region,
        },
      )

      return this.updateFolderNameInList({ newName, folderId })
    },

    async moveAssets({
      spaceId,
      payload,
      isAll,
      assetIds,
    }: {
      spaceId: number
      payload: { asset: { asset_folder_id: number } }
      isAll: boolean
      assetIds: number[]
    }): Promise<unknown> {
      if (isAll) {
        const promisesThrottle = new Throttle({
          requestsPerSecond: 3,
        })

        const promises = assetIds.map((assetId) => {
          return () =>
            client.put(
              `spaces/${spaceId}/assets/${assetId}`,
              {
                asset: { asset_folder_id: '' },
              },
              {
                region: useSpace().region,
              },
            )
        })

        return promisesThrottle.addAll(promises)
      }

      return await client.post(
        `spaces/${spaceId}/assets/bulk_update`,
        payload,
        {
          region: useSpace().region,
        },
      )
    },

    async moveFolder({
      spaceId,
      folderId,
      payload,
    }: {
      spaceId: number
      folderId: number
      payload: { asset_folder: { parent_id: number } }
    }): Promise<void> {
      await client.put(`spaces/${spaceId}/asset_folders/${folderId}`, payload, {
        region: useSpace().region,
      })
      return this.loadAssetFolders(spaceId)
    },

    async deleteAssets({
      spaceId,
      assetIds = [],
    }: {
      spaceId: Space['id']
      assetIds: Array<Asset['id']>
    }): Promise<void> {
      if (assetIds.length === 0) {
        return Promise.reject(new Error('No asset selected'))
      } else {
        try {
          return await client.post(
            `spaces/${spaceId}/assets/bulk_destroy`,
            {
              ids: assetIds,
            },
            {
              region: useSpace().region,
            },
          )
        } catch (e) {
          return Promise.reject(e)
        }
      }
    },

    async restoreAssets({
      spaceId,
      assetIds = [],
    }: {
      spaceId: number
      assetIds: number[]
    }): Promise<void> {
      if (assetIds.length === 0) {
        return Promise.reject(new Error('No asset selected'))
      } else {
        try {
          return await client.post(
            `spaces/${spaceId}/assets/bulk_restore`,
            {
              ids: assetIds,
            },
            {
              region: useSpace().region,
            },
          )
        } catch (e) {
          return Promise.reject(e)
        }
      }
    },

    async deleteAssetFolder({
      spaceId,
      folderId,
      recursive,
    }: {
      spaceId: number
      folderId: number[]
      recursive?: boolean
    }): Promise<void> {
      if (!folderId) {
        return Promise.reject(new Error('No folder selected'))
      } else {
        try {
          const recursiveDeleteParam = recursive
            ? `?recursive=${recursive}`
            : ''
          return await client.delete(
            `spaces/${spaceId}/asset_folders/${folderId}${recursiveDeleteParam}`,
            {
              region: useSpace().region,
            },
          )
        } catch (e) {
          return Promise.reject(e)
        }
      }
    },

    async clearAssetsTrash({ spaceId }: { spaceId: number }): Promise<void> {
      return await client.get(`spaces/${spaceId}/assets/clear_trash`, {
        region: useSpace().region,
      })
    },
  },
})
